import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * @typedef {Object} SearchBarPredictiveRefs
 * @property {HTMLInputElement} input - The search input element.
 * @property {HTMLElement} results - The results dropdown container.
 */

/**
 * Escapes HTML special characters to avoid injecting markup from resource data.
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

/**
 * A custom element that fetches and renders live product suggestions
 * (title + description match) beneath an inline search bar input.
 *
 * @extends {Component<SearchBarPredictiveRefs>}
 */
class SearchBarPredictive extends Component {
  requiredRefs = ['input', 'results'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleOutsideClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) {
      this.#closeResults();
    }
  };

  /**
   * @param {Event} event
   */
  onInput = debounce((event) => {
    const term = this.refs.input.value.trim();

    if (term.length < 2) {
      this.#closeResults();
      return;
    }

    this.#fetchResults(term);
  }, 250);

  /**
   * @param {KeyboardEvent} event
   */
  onKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#closeResults();
      return;
    }

    const items = this.#items;
    if (!items.length) return;

    const currentIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#highlight(items, currentIndex < items.length - 1 ? currentIndex + 1 : 0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#highlight(items, currentIndex > 0 ? currentIndex - 1 : items.length - 1);
        break;
      case 'Enter':
        if (currentIndex >= 0) {
          event.preventDefault();
          const link = items[currentIndex]?.querySelector('a');
          if (link instanceof HTMLAnchorElement) link.click();
        }
        break;
    }
  };

  get #items() {
    return Array.from(this.refs.results.querySelectorAll('[role="option"]'));
  }

  /**
   * @param {Element[]} items
   * @param {number} index
   */
  #highlight(items, index) {
    items.forEach((item, i) => {
      item.setAttribute('aria-selected', String(i === index));
    });
    items[index]?.scrollIntoView({ block: 'nearest' });
  }

  /**
   * @param {string} term
   */
  async #fetchResults(term) {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    const url =
      `/search/suggest.json?q=${encodeURIComponent(term)}` +
      '&resources[type]=product' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,body,vendor,product_type';

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Search request failed');
      const data = await response.json();
      if (controller.signal.aborted) return;
      const products = data?.resources?.results?.products ?? [];
      this.#renderResults(products);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#closeResults();
    }
  }

  /**
   * @param {Array<{title: string, url: string, image?: string, price?: string}>} products
   */
  #renderResults(products) {
    const { results } = this.refs;

    if (!products.length) {
      const message = this.dataset.noResultsText || 'No products found';
      results.innerHTML = `<li class="search-bar__no-results">${escapeHtml(message)}</li>`;
      results.hidden = false;
      return;
    }

    results.innerHTML = products
      .map((product) => {
        const image = product.image
          ? `<img src="${escapeHtml(product.image)}" alt="" class="search-bar__result-image" width="40" height="40" loading="lazy">`
          : '';
        const price = product.price ? `<span class="search-bar__result-price">${escapeHtml(product.price)}</span>` : '';

        return `
          <li class="search-bar__result" role="option">
            <a href="${escapeHtml(product.url)}" class="search-bar__result-link">
              ${image}
              <span class="search-bar__result-info">
                <span class="search-bar__result-title">${escapeHtml(product.title)}</span>
                ${price}
              </span>
            </a>
          </li>
        `;
      })
      .join('');

    results.hidden = false;
  }

  #closeResults() {
    const { results } = this.refs;
    results.hidden = true;
    results.innerHTML = '';
  }
}

if (!customElements.get('search-bar-predictive')) {
  customElements.define('search-bar-predictive', SearchBarPredictive);
}
